import { McSpvSurveyRequest } from '../interface/multi-country.interface';
import { BuyerSurveyResponse } from '../../../../../shared/interfaces/survey.interface';
import { MultiCountrySpvSurveyPayloadMapper } from './multi-country-single-view-data-mapper';
import { UserType } from '../../../../../shared/types/user-type';
import { McSvpParentChildSegregator } from '../../../../../shared/domain/mc-svp-parent-child-segregator';

type McSvpPayloadSetterEach = {
  for: 'each';
  data: Partial<BuyerSurveyResponse>[];
};

type McSvpPayloadSetterAll = {
  for: 'all';
  data: Partial<Omit<BuyerSurveyResponse, 'survey_localization'>>;
};

export type McSvpSetterData = McSvpPayloadSetterEach | McSvpPayloadSetterAll;

export type McSvpLocaleRemoverData = {
  data: string[];
};
export class McSvpSurveyPayloadComposer {
  private _mcSvpChildSurveys: BuyerSurveyResponse[] = [];
  private _mcSvpParentSurvey = <BuyerSurveyResponse>{};

  constructor(
    private readonly _userType: UserType,
    private readonly _isServiceUser: boolean = false
  ) {}

  public initial(mcSvpSurveyData: BuyerSurveyResponse[]): void {
    const segregator = new McSvpParentChildSegregator(mcSvpSurveyData);
    this._mcSvpChildSurveys = segregator.childs();
    this._mcSvpParentSurvey = segregator.parent();
  }

  public set(value: McSvpSetterData) {
    if (this._shouldSetAll(value)) {
      this._setAll(value.data);
      return;
    }

    this._setEach(value.data);
  }

  public remove(value: McSvpLocaleRemoverData) {
    this._removeLocale(value.data);
  }

  private _shouldSetAll(
    value: McSvpSetterData
  ): value is McSvpPayloadSetterAll {
    return (value as McSvpPayloadSetterAll).for === 'all';
  }

  private _setAll(
    data: Partial<Omit<BuyerSurveyResponse, 'survey_localization'>>
  ): void {
    this._mcSvpChildSurveys = this._mcSvpChildSurveys.map((eachSurvey) => ({
      ...eachSurvey,
      ...data,
    }));

    this._mcSvpParentSurvey = {
      ...this._mcSvpParentSurvey,
      ...data,
      completes_required: this._parentCompletes(this._mcSvpChildSurveys),
    };
  }

  private _setEach(data: Partial<BuyerSurveyResponse>[]): void {
    this._mcSvpChildSurveys = this._mcSvpChildSurveys.map((eachSurvey) => {
      const found = data.find(
        (s) => s.survey_localization === eachSurvey.survey_localization
      );
      if (found) {
        return {
          ...eachSurvey,
          ...found,
        };
      }
      return eachSurvey;
    });

    this._mcSvpParentSurvey = {
      ...this._mcSvpParentSurvey,
      completes_required: this._parentCompletes(this._mcSvpChildSurveys),
    };
  }

  private _parentCompletes(childs: BuyerSurveyResponse[]): number {
    return childs.reduce(
      (total, current) => total + (current?.completes_required || 0),
      0
    );
  }

  private _surveys(): BuyerSurveyResponse[] {
    if (this.surveyId()) {
      return [this._mcSvpParentSurvey, ...this._mcSvpChildSurveys];
    }
    return [];
  }

  public payload(): McSpvSurveyRequest | null {
    if (!this._surveys().length) {
      return null;
    }

    const mapper = new MultiCountrySpvSurveyPayloadMapper(
      this._surveys(),
      this._userType,
      this._isServiceUser
    );
    return mapper.transform();
  }

  public updated(): BuyerSurveyResponse[] {
    return this._surveys();
  }

  private _removeLocale(localizationsToRemove: string[]): void {
    this._mcSvpChildSurveys = this._mcSvpChildSurveys.filter(
      (childSurvey) =>
        !localizationsToRemove.includes(childSurvey.survey_localization)
    );
  }

  public surveyId(): number {
    return this._mcSvpParentSurvey.ps_survey_id;
  }

  public surveyStatus(): number {
    return this._mcSvpParentSurvey.ps_survey_status;
  }
}
